import { Paper, Stack, Typography, Box } from '@mui/material';
import { ReactNode } from 'react';
import { BaseFieldProperties } from 'src/api/process-module';
import { FieldComponentConfig } from './FieldNameLabelInputs';

interface BaseFieldWrapperProps {
  label?: string | ReactNode | null | undefined;
  children: ReactNode;
  description?: string;
  required?: boolean;
  fieldProps?: BaseFieldProperties;
  error?: string;
  fullWidth?: boolean;
  fieldConfiguration?: FieldComponentConfig;
}

export const BaseFieldWrapper = ({
  label,
  children,
  description,
  required = false,
  fieldProps,
  error,
  fullWidth = true,
  fieldConfiguration,
}: BaseFieldWrapperProps) => {
  // Apply container styles
  const containerStyle = {
    width: fullWidth ? '100%' : 'auto',
  };

  // Use the provided label directly
  const displayLabel = label;

  // Check if label is a ReactNode (not a string)
  const isReactNodeLabel = typeof displayLabel !== 'string' && displayLabel !== null && displayLabel !== undefined;

  return (
    <Box sx={containerStyle}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 1,
          backgroundColor: 'background.paper',
          border: (theme) => (error ? `1px solid ${theme.palette.error.main}` : '1px solid rgba(0, 0, 0, 0.12)'),
        }}
      >
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: description ? 0.5 : 1 }}>
            {!fieldConfiguration?.hideLabel && (
              <>
                {isReactNodeLabel ? (
                  // Render ReactNode label directly
                  <Box
                    sx={{
                      fontWeight: 500,
                      color: (theme) => (error ? theme.palette.error.main : 'text.primary'),
                    }}
                  >
                    {displayLabel}
                  </Box>
                ) : (
                  // Render string label with Typography
                  <Typography
                    variant="subtitle1"
                    component="label"
                    sx={{
                      fontWeight: 500,
                      color: (theme) => (error ? theme.palette.error.main : 'text.primary'),
                    }}
                  >
                    {displayLabel}
                    {required && (
                      <Typography component="span" color="error" sx={{ ml: 0.5 }}>
                        *
                      </Typography>
                    )}
                  </Typography>
                )}
              </>
            )}
          </Stack>

          {description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {description}
            </Typography>
          )}

          {children}

          {error && (
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};
